import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';
import { motion } from 'framer-motion';

import { useToast } from '../components/common/ToastProvider';
import styled from 'styled-components';
import { FaSlidersH, FaTimes } from 'react-icons/fa';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  Button,
  fadeIn,
  Modal,
  SubSectionTitle,
  Textarea,
  Divider,
  PipelineButtons
} from '../styles/PageStyles';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChalkboardTeacher, faTrash, faBroom, faTimes, faPlay, faSave, faCog, faChevronCircleLeft, faEye, faEdit } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Node types
import AgentNode from '../components/pipeline/AgentNode';
import ModelNode from '../components/pipeline/ModelNode';
import KnowledgeNode from '../components/pipeline/KnowledgeNode';
import ToolNode from '../components/pipeline/ToolNode';
import TriggerNode from '../components/pipeline/TriggerNode';
import OutputNode from '../components/pipeline/OutputNode';
import ApiNode from '../components/pipeline/ApiNode';
import ClassifierNode from '../components/pipeline/ClassifierNode';
import CodeNode from '../components/pipeline/CodeNode';
import WebHookNode from '../components/pipeline/WebHookNode';

// Sidebar components
import NodePalette from '../components/pipeline/NodePalette';
import PropertiesPanel from '../components/pipeline/PropertiesPanel';
import { useNavigate, useParams } from 'react-router-dom';
import { createPipeline, getNodesForPipeline, getPipelines, runExecutePipeline, resumePipeline, updatePipeline, getPipelineExecutionStatus, getPipelineRuns, getFileNodeConfig } from '../services/pipelineService';
import DataSourceNode from '../components/pipeline/DataSourceNode';
import { uploadFile } from '../services/fileUploadService';
import PipelineExecutionDrawer from '../components/pipeline/PipelineExecutionDrawer';
import EmailOutNode from '../components/pipeline/EmailOutNode';
import FileOperationsNode from '../components/pipeline/FileOperationsNode';
import TaskServiceNode from '../components/pipeline/TaskServiceNode';
import { convertNodeNameToLocal, mandatoryFields, nodeDefaults } from '../constants/nodeDefaults';
import TimerNode from '../components/pipeline/TimerNode';


const NodePaletteContainer = styled.div`
  width: 245px;
  min-width: 220px;
  max-width: 400px;
  background: #f6f8fa;
  border-right: 1px solid #eee;
  height: 100%;
  z-index: 1;
`;

const FlowContainer = styled.div`
  width: calc(100% - 245px);
  height: calc(100dvh - 170px);
  position: relative;
`;

const FloatingDrawer = styled.div`
  position: fixed;
  top: 155px; 
  right: 0;
  height: calc(100vh - 144px);
  background: #fff;
  border-left: 1px solid #eee;
  box-shadow: #b7b7b7 -2px 12px 13px;
  min-width: 280px;
  max-width: 700px;
  width: ${props => props.width}px;
  transition: width 0.2s;
  z-index: 1002;
  display: ${props => (props.open ? 'block' : 'none')};
  overflow: auto;
`;

const DrawerToggleButton = styled.button`
  position: fixed;
  top: 160px;
  right: ${props => (props.drawerOpen ? `${props.drawerWidth + 10}px` : '10px')};
  z-index: 1003;
  background: #ffffff;
  color: #666;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  cursor: pointer;
`;

const DrawerResizer = styled.div`
  width: 6px;
  cursor: ew-resize;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #e0e0e0;
  z-index: 1004;
  &:hover {
    background: #bdbdbd;
  }
`;


const nodeTypes = {
  triggerNode: TriggerNode,
  outputNode: OutputNode,
  agentNode: AgentNode,
  modelNode: ModelNode,
  knowledgeNode: KnowledgeNode,
  dataSourceNode: DataSourceNode,
  apiNode: ApiNode,
  taskServiceNode:TaskServiceNode,
  timerNode: TimerNode,
  classifierNode: ClassifierNode,
  emailOutNode: EmailOutNode,
  codeNode: CodeNode,
  webHookNode: WebHookNode,
  toolNode: ToolNode,
  fileNode: FileOperationsNode
};

const DesignerContainer = styled.div`
  display: flex;
  height: calc(100vh - 160px);
  background: #f9fafb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

// Markdown editor container with preview toggle
const MarkdownEditorContainer = styled.div`
  position: relative;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  height: 200px;
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: ${props => props.active ? 'var(--primary)' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : '#ccc'};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-dark)' : '#e9ecef'};
  }
`;

const MarkdownPreview = styled.div`
  padding: 0.75rem;
  height: 100%;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  line-height: 1.6;
  
  h1, h2, h3, h4, h5, h6 { margin: 0.5em 0; }
  p { margin: 0.5em 0; }
  ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
  code { background: #f1f3f4; padding: 2px 4px; border-radius: 3px; }
  pre { background: #f8f9fa; padding: 1rem; border-radius: 4px; overflow-x: auto; }
  blockquote { border-left: 4px solid #dee2e6; padding-left: 1rem; margin: 1rem 0; }
`;

// const FlowContainer = styled.div`
//   flex: 1;
//   height: 100%;
// `;

const DesignerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
`;

const PipelineName = styled.input`
  border: none;
  border-bottom: 2px solid #ccc;
  color: #333 !important; /* Force dark text color */
  position: relative;
  top: -5px;
font-size: 1.8rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: 'Orbitron', sans-serif;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const PipelineDesigner = () => {
  const navigate = useNavigate();
  const { pipelineId } = useParams();
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [pipelineName, setPipelineName] = useState('New Pipeline');
  const [pipelineInstanceId, setPipelineInstanceId] = useState(null);
  const [pipelineDescription, setPipelineDescription] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [executionResults, setExecutionResults] = useState([]);
  const [totalExecutionResults, setTotalExecutionResults] = useState(0);
  const [pipelineLoader, setPipelineLoader] = useState(false);
  // Add these state variables at the top with other state declarations
  const [showRunModal, setShowRunModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [pipelineInput, setPipelineInput] = useState('{\n  "key": "value"\n}');
  const [resumeInput, setResumeInput] = useState('{\n  "key": "value"\n}');
  const [uploadedDocuments, setUploadedDocuments] = useState([]); // For multiple files
  const [resumeUploadedDocuments, setResumeUploadedDocuments] = useState([]); // For resume modal files
  const [isRunning, setIsRunning] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pipelineRunFlag, setPipelineRunFlag] = useState(false);
  const [runError, setRunError] = useState(null);
  const [pageNumberNode, setPageNumberNode] = useState(0);
  const [pageSizeNode, setPageSizeNode] = useState(10);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [docUrls, setDocUrls] = useState([])
  const [inputMode, setInputMode] = useState('json'); // 'json' or 'markdown'
  const [isRunPreviewMode, setIsRunPreviewMode] = useState(false);
  const [isResumePreviewMode, setIsResumePreviewMode] = useState(false);

  const { showToast } = useToast();

  // Fetch pipeline data if editing
  useEffect(() => {
    const fetchPipelineById = async () => {
      try {
        let response = await getPipelines({ code: pipelineId });
        let pipeline = response[0];
        setPipelineName(pipeline.name);
        setPipelineDescription(pipeline.description);
        setSelectedPipeline(pipeline);
        let convertedPayload = convertPipelineToReactFlow(pipeline);
        setNodes(convertedPayload.nodes);
        setEdges(convertedPayload.edges);
      } catch (error) {
        console.error('Error fetching pipeline:', error);
      }
    };

    if (pipelineId !== 'new') {
      fetchPipelineById();
    } else {
      setNodes([]);
      setEdges([]);
      setPipelineName('New Pipeline');
      setPipelineDescription('');
    }
  }, [pipelineId]);

  // Keep selectedNode in sync with nodes
  useEffect(() => {
    if (selectedNode) {
      const updatedNode = nodes.find((node) => node.id === selectedNode.id);
      if (updatedNode) {
        setSelectedNode(updatedNode);
      }
    }
  }, [nodes, selectedNode]);

  // Keep selectedEdge in sync with edges (fixes textarea cursor jump)
  useEffect(() => {
    if (selectedEdge) {
      const updated = edges.find(e => e.id === selectedEdge.id);
      if (updated) {
        // Only update if the conditions array reference has changed
        if (updated.data?.conditions !== selectedEdge.data?.conditions) {
          setSelectedEdge(updated);
        }
      } else {
        setSelectedEdge(null);
      }
    }
  }, [edges, selectedEdge]);

  const onConnect = useCallback((params) => {
    setEdges((eds) =>
      addEdge(
        {
          ...params,
          animated: true,
          style: { stroke: '#0066ff', strokeWidth: 2 }
        },
        eds
      )
    );
  }, [setEdges]);

  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null); // Deselect node if edge is selected
    setDrawerOpen(true)
  }, []);

  const onInit = useCallback((instance) => {
    setReactFlowInstance(instance);
  }, []);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
    // console.log(node, selectedNode)
    setDrawerOpen(true)
  }, []);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance || typeof reactFlowInstance.project !== 'function') {
        console.error('ReactFlow instance is not ready or missing .project()');
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      const nodeData = JSON.parse(event.dataTransfer.getData('application/nodeData'));

      if (!type) return;

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top
      });

      console.log("type--", type)

      const defaultPropsValues = nodeDefaults[type] || { };

      const newId = `${type}-${Date.now()}`;
      const newNode = {
        id: newId,
        type,
        position,
        data: {
          ...nodeData,
          name: `${nodeData.name} ${nodes.filter(n => n.type === type).length + 1}`,
          code: newId,
          check_list: "",
          post_processors: [],
          pre_procesors: [],
          data: {...defaultPropsValues.data },
          position: {},
          positionAbsolute: {}
        }
      };

      setNodes((nds) => {
        nds = nds.concat(newNode)
        return nds
      });
    },
    [reactFlowInstance, nodes, setNodes]
  );

  const updateNodeProperties = useCallback((id, data) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? { ...node, data: { ...node.data, ...data } }
          : node
      )
    );
  }, [setNodes]);

  const convertReactFlowToPipeline = useCallback((reactFlowData) => {
    const { nodes, edges, name } = reactFlowData;
    const pipelineNodes = nodes.map((node) => ({
      code: node.code,
      name: node.name,
      description: node.description,
      type: node.type,
      agent_code: node.agent_code || null,
      check_list: node.check_list || null,
      pre_procesors: node.pre_procesors || [],
      post_processors: node.post_processors || [],
      data: node.data || {},
      position: node.position || {},
      positionAbsolute: node.positionAbsolute || {},
      ...(node.mcp_servers ? { mcp_servers: node.mcp_servers } : {})
    }));

    const pipelineEdges = edges.map((edge) => ({
      from: edge.source,
      to: edge.target,
      conditions: edge.data?.conditions || ["true"]
    }));

    return {
      code: selectedPipeline.code,
      name: name || "New Pipeline",
      description: selectedPipeline.description || '',
      nodes: pipelineNodes,
      edges: pipelineEdges
    };
  }, [selectedPipeline]);

  const convertPipelineToReactFlow = (pipelineData) => {
    const { nodes, edges, name } = pipelineData;

    const getReactType = (source) => {
      switch (source) {
        case 'trigger': return 'triggerNode';
        case 'decision_agent': return 'agentNode';
        case 'model': return 'modelNode';
        case 'knowledgebase': return 'knowledgeNode';
        case 'source': return 'dataSourceNode';
        case 'API': return 'apiNode';
        case 'api': return 'apiNode';
        case 'classify': return 'classifierNode';
        case 'webhook': return 'webHookNode';
        case 'emailout': return 'emailOutNode';
        case 'code': return 'codeNode';
        case 'output': return 'outputNode';
        case 'file_input': return 'fileNode';
        case 'task_service': return 'taskServiceNode';
        case 'timer_node' : return 'timerNode';
        default: return source;
      }
    };
    const reactFlowNodes = nodes.map((node) => ({
      id: node.code,
      type: getReactType(node.type),
      position: (node.position === undefined || JSON.stringify(node.position) === '{}') ? { x: Math.random() * 400, y: Math.random() * 400 } : node.position,
      data: { ...node, label: node.name }
    }));

    const reactFlowEdges = edges.map((edge) => ({
      id: `${edge.from}-${edge.to}`,
      source: edge.from,
      target: edge.to,
      animated: true,
      style: { stroke: "#0066ff", strokeWidth: 2 },
      data: { conditions: edge.conditions || ["true"] }
    }));

    return {
      name: name || "New Pipeline",
      nodes: reactFlowNodes,
      edges: reactFlowEdges
    };
  };
  const runPipeline = useCallback(() => {
    setShowRunModal(true);
  }, []);
  const openPropertiesWithoutNodeSelected = useCallback(() => {
    setSelectedNode(null);
    setDrawerOpen(true);
  }, []);
  const handleResumePipeline = useCallback(() => {
    setShowResumeModal(true);
  }, []);

  // Utility function to merge new results with existing ones, avoiding duplicates
  const mergeUniqueResults = useCallback((currentResults, newResults, replaceAll = false) => {
    if (!newResults || newResults.length === 0) {
      return replaceAll ? [] : currentResults;
    }

    if (replaceAll) {
      return newResults;
    }

    const existingIds = new Set(currentResults.map(result => result.id));
    const uniqueNewResults = newResults.filter(result => !existingIds.has(result.id));

    return uniqueNewResults.length > 0 ? [...currentResults, ...uniqueNewResults] : currentResults;
  }, []);
 
  // Poll node execution results for the drawer 
  const updateExecutionResults = useCallback(async (pipelineId) => {
    if (!pipelineId) {
      console.warn('Attempted to update execution results without pipeline ID');
      return;
    }
    
    try {
      const filter = { pipeline_id: pipelineId };
      const sort = { created_at: "ASC" };
      const requestId = `node_execution_${pipelineId}_${Date.now()}`;
      
      // Using requestId to help avoid duplicate requests
      const response = await getNodesForPipeline(
        filter, 
        sort, 
        `?skip=0&limit=${pageSizeNode}`,
        requestId
      );

      if (response && Array.isArray(response)) {
        const validatedNodes = response.map((node, index) => {
          if (!node || typeof node !== 'object') {
            console.warn(`Invalid node at index ${index}:`, node);
            return null;
          }

          return {
            id: node.id || `node_${index}`,
            x: node.x || 0,
            y: node.y || 0,
            type: node.type || 'default',
            data: node.data || {},
            position: node.position || { x: 0, y: 0 },
            ...node
          };
        }).filter(Boolean);

        setExecutionResults(validatedNodes);

        setPageNumberNode(0);
        setIsLoadingMore(false);

        if (validatedNodes.length < pageSizeNode) {
          setHasMorePages(false);
        } else {
          setHasMorePages(true);
        }
      }
    } catch (error) {
      // Don't show error for canceled requests
      if (!axios.isCancel(error)) {
        console.error('Error updating execution results:', error);
      }
    }
  }, [pageSizeNode]);

  const checkPipelineStatus = useCallback(async (pipelineId) => {
    try {
      const status = await getPipelineExecutionStatus(pipelineId);
      setPipelineStatus(status);

      if (status && status.transaction_status) {
        const endStates = ['COMPLETED', 'CLOSED', 'ERROR', 'ERRORED', 'WAITING'];
        if (endStates.includes(status.transaction_status.toUpperCase())) {
          // Stop polling if in an end state
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          setIsRunning(false);

          if (status.transaction_status.toUpperCase() === 'COMPLETED') {
            showToast({
              message: 'Pipeline completed successfully!',
              type: 'success',
              duration: 5000
            });
          } else if (['ERROR', 'ERRORED'].includes(status.transaction_status.toUpperCase())) {
            showToast({
              message: `Pipeline failed with status: ${status.transaction_status}`,
              type: 'error',
              duration: 8000
            });
          } else if (status.transaction_status.toUpperCase() === 'WAITING') {
            showToast({
              message: 'Pipeline is waiting for input or external trigger',
              type: 'info',
              duration: 5000
            });
          } else {
            showToast({
              message: `Pipeline finished with status: ${status.transaction_status}`,
              type: 'info',
              duration: 5000
            });
          }
          
          // Final execution results update when pipeline is done
          // We'll handle this in the polling function to avoid duplicate requests
          // No need to call updateExecutionResults here
        }
      }

      return status;
    } catch (error) {
      console.error('Error checking pipeline status:', error);
      return null;
    }
  }, [pollingInterval, showToast]);

  // Polling pipeline status
  const startPolling = useCallback((pipelineId) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    (async () => {
      try {
        const status = await checkPipelineStatus(pipelineId);
        
        // Only fetch execution results if we need to
        if (status && !['COMPLETED', 'CLOSED', 'ERROR', 'ERRORED'].includes(status.transaction_status?.toUpperCase())) {
          await updateExecutionResults(pipelineId);
        }
      } catch (error) {
        console.error("Initial polling error:", error);
      }
    })();

    // Then set up the interval for future updates
    const interval = setInterval(async () => {
      try {
        const status = await checkPipelineStatus(pipelineId);
        
        // Only fetch execution results if pipeline is still running
        if (status && !['COMPLETED', 'CLOSED', 'ERROR', 'ERRORED'].includes(status.transaction_status?.toUpperCase())) {
          await updateExecutionResults(pipelineId);
        }
      } catch (error) {
        console.error("Polling interval error:", error);
      }
    }, 10000);

    setPollingInterval(interval);
  }, [pollingInterval, checkPipelineStatus, updateExecutionResults]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setIsRunning(false);
  }, [pollingInterval]);

  // Clear polling 
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  //  Clear polling 
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const executePipeline = async () => {
    setIsRunning(true);
    setRunError(null);

    try {
      let inputData;
      
      // Validate input based on input mode
      if (inputMode === 'json') {
        // JSON validation
        if (!pipelineInput.trim()) {
          throw new Error('Pipeline input cannot be empty when using JSON mode');
        }
        try {
          inputData = JSON.parse(pipelineInput);
          if (inputData === null || inputData === undefined) {
            throw new Error('JSON input cannot be null or undefined');
          }
        } catch (error) {
          throw new Error(`Invalid JSON input: ${error.message}`);
        }
      } else if (inputMode === 'markdown') {
        // Markdown validation
        if (!pipelineInput || pipelineInput.trim() === '') {
          throw new Error('Pipeline input cannot be empty when using Markdown mode');
        }
        // For markdown, we'll pass the raw markdown content as a string
        // wrapped in a structure that the pipeline can understand
        inputData = {
          markdown: pipelineInput.trim()
        };
      } else {
        throw new Error('Invalid input mode selected');
      }
      
      let documentUrls = [];

      if (uploadedDocuments.length > 0) {
        setPipelineRunFlag(true);

        const timestamp = Date.now();

        try {
          if (manualRun) {
            const uploadResults = await Promise.all(
              uploadedDocuments.map((doc, index) => {
                // Add null safety and fallback naming
                const fileName = doc.name || `document_${index}`;
                const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '');
                const fileId = `${timestamp}_${cleanFileName}`;

                return uploadFile(
                  doc,
                  "runpipeline",
                  pipelineName,
                  fileId
                );
              })
            );

            const failedUploads = uploadResults.filter(result => result.status !== 'success');
            if (failedUploads.length > 0) {
              const errorMessages = failedUploads.map(result => result.message || 'Unknown error');
              throw new Error(`Document upload failed: ${errorMessages.join(', ')}`);
            }
            documentUrls = uploadResults.map(result => result.data?.url).filter(Boolean);
            if (documentUrls.length !== uploadResults.length) {
              throw new Error('Some uploaded documents did not return valid URLs');
            }
          } else {
            documentUrls = docUrls;
          }

        } catch (error) {
          throw new Error(`Failed to upload documents: ${error.message}`);
        }
      }

      const pipelinePayload = {
        pipeline_id: null,
        document_urls: documentUrls,
        payload: inputData,
        async_execution: true
      };

      const executionResult = await runExecutePipeline(pipelineId, pipelinePayload);

      if (!executionResult || !executionResult.pipeline_id) {
        throw new Error('Pipeline execution failed - no pipeline ID returned');
      }

      showToast({
        message: 'Pipeline started successfully! Checking status...',
        type: 'success',
        duration: 5000,
        onClose: () => {
          console.log('Pipeline execution toast closed');
        }
      });

      startPolling(executionResult.pipeline_id);

      // Reset form state
      setPipelineInput('{\n  "key": "value"\n}');
      setUploadedDocuments([]);

      const filter = { pipeline_id: executionResult.pipeline_id };
      const sort = { created_at: "ASC" };

      // Fetch new nodes from this pipeline execution
      const requestId = `execute_pipeline_${executionResult.pipeline_id}_${Date.now()}`;
      const newNodesResponse = await getNodesForPipeline(
        filter,
        sort,
        `?skip=0&limit=${pageSizeNode}`,
        requestId
      );

      if (!newNodesResponse || !Array.isArray(newNodesResponse)) {
        console.warn('Invalid nodes response:', newNodesResponse);
        throw new Error('Failed to fetch pipeline nodes - invalid response format');
      }

      const validatedNewNodes = newNodesResponse.map((node, index) => {
        if (!node || typeof node !== 'object') {
          console.warn(`Invalid node at index ${index}:`, node);
          return null;
        }

        return {
          id: node.id || `node_${index}`,
          x: node.x || 0,
          y: node.y || 0,
          type: node.type || 'default',
          data: node.data || {},
          position: node.position || { x: 0, y: 0 },
          ...node
        };
      }).filter(Boolean);

      const uniqueResults = mergeUniqueResults(nodes, validatedNewNodes, false);
      console.log({ uniqueResults })

      setPipelineInstanceId(executionResult.pipeline_id);
      setNodes(uniqueResults);

      setExecutionResults(validatedNewNodes);

      setPageNumberNode(0);
      setIsLoadingMore(false);

      if (validatedNewNodes.length < pageSizeNode) {
        setHasMorePages(false);
      } else {
        setHasMorePages(true);
      }

      setAnalyticsDrawerOpen(true);
      setShowRunModal(false);

    } catch (error) {
      console.error('Pipeline execution failed:', error);

      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      // Set user-friendly error message
      const errorMessage = error.message || 'An unexpected error occurred during pipeline execution';
      setRunError(errorMessage);

      showToast({
        message: errorMessage,
        type: 'error',
        duration: 8000
      });

    } finally {
      setIsRunning(false);
    }
  };

  const resumePipelineExecution = async () => {
    setIsRunning(true);
    setRunError(null);

    try {
      let inputData;
      
      // Validate input based on input mode
      if (inputMode === 'json') {
        // JSON validation
        if (!resumeInput.trim()) {
          throw new Error('Resume input cannot be empty when using JSON mode');
        }
        try {
          inputData = JSON.parse(resumeInput);
          if (inputData === null || inputData === undefined) {
            throw new Error('JSON input cannot be null or undefined');
          }
        } catch (error) {
          throw new Error(`Invalid JSON input: ${error.message}`);
        }
      } else if (inputMode === 'markdown') {
        // Markdown validation
        if (!resumeInput || resumeInput.trim() === '') {
          throw new Error('Resume input cannot be empty when using Markdown mode');
        }
        // For markdown, we'll pass the raw markdown content as a string
        // wrapped in a structure that the pipeline can understand
        inputData = {
          content: resumeInput.trim(),
          content_type: 'markdown'
        };
      } else {
        throw new Error('Invalid input mode selected');
      }

      let documentUrls = [];

      if (resumeUploadedDocuments.length > 0) {
        const timestamp = Date.now();

        try {
          const uploadResults = await Promise.all(
            resumeUploadedDocuments.map((doc, index) => {
              const fileName = doc.name || `document_${index}`;
              const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '');
              const fileId = `${timestamp}_${cleanFileName}`;

              return uploadFile(
                doc,
                "resumepipeline",
                pipelineName,
                fileId
              );
            })
          );

          const failedUploads = uploadResults.filter(result => result.status !== 'success');
          if (failedUploads.length > 0) {
            const errorMessages = failedUploads.map(result => result.message || 'Unknown error');
            throw new Error(`Document upload failed: ${errorMessages.join(', ')}`);
          }

          documentUrls = uploadResults.map(result => result.data?.url).filter(Boolean);

          if (documentUrls.length !== uploadResults.length) {
            throw new Error('Some uploaded documents did not return valid URLs');
          }

        } catch (error) {
          throw new Error(`Failed to upload documents: ${error.message}`);
        }
      }

      const resumePayload = {
        pipeline_id: pipelineInstanceId,
        document_urls: documentUrls,
        payload: inputData,
        async_execution: true
      };

      const resumeResult = await resumePipeline(pipelineInstanceId, resumePayload);

      if (!resumeResult) {
        throw new Error('Pipeline resume failed - no response returned');
      }

      showToast({
        message: 'Pipeline resumed successfully! Checking status...',
        type: 'success',
        duration: 5000
      });

      // Start polling for the resumed pipeline
      startPolling(pipelineInstanceId);

      // Reset form state
      setResumeInput('{\n  "key": "value"\n}');
      setResumeUploadedDocuments([]);

      // Update execution results
      await updateExecutionResults(pipelineInstanceId);

      setShowResumeModal(false);

    } catch (error) {
      console.error('Pipeline resume failed:', error);

      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      const errorMessage = error.message || 'An unexpected error occurred during pipeline resume';
      setRunError(errorMessage);

      showToast({
        message: errorMessage,
        type: 'error',
        duration: 8000
      });

    } finally {
      setIsRunning(false);
    }
  };

  const refreshResults = useCallback(async () => {
    setPipelineLoader(true);
    if (!pipelineInstanceId) return;

    try {
      // Check status and pdate results
      if (pipelineInstanceId) {
        await checkPipelineStatus(pipelineInstanceId);
      }

      await updateExecutionResults(pipelineInstanceId);

      setPipelineLoader(false);
    }
    catch (error) {
      setPipelineLoader(false);
      console.error('Error refreshing results:', error);
      setRunError(error.message || 'Failed to refresh results');
    }
  }, [pipelineInstanceId, checkPipelineStatus, updateExecutionResults]);

  // Handle pagination for infinite scroll
  const handlePaginationChange = useCallback(async (event) => {
    if (event === 'next' && !isLoadingMore && hasMorePages) {
      setIsLoadingMore(true);
      const nextPage = pageNumberNode + 1;

      try {
        const filter = { pipeline_id: pipelineInstanceId };
        const sort = { created_at: "ASC" };

        // Fetch next page with skip and limit
        const requestId = `pagination_${pipelineInstanceId}_${nextPage}_${Date.now()}`;
        const response = await getNodesForPipeline(
          filter,
          sort,
          `?skip=${nextPage * pageSizeNode}&limit=${pageSizeNode}`,
          requestId
        );

        if (response && response.length > 0) {
          // Use utility function to merge unique results
          setExecutionResults(prevResults => mergeUniqueResults(prevResults, response));
          setPageNumberNode(nextPage);

          if (response.length < pageSizeNode) {
            console.log('Reached end of data - no more pages available');
            setHasMorePages(false);
          }
        } else {
          // No data returned, reached the end
          setHasMorePages(false);
        }
      } catch (error) {
        // Don't log canceled requests as errors
        if (!axios.isCancel(error)) {
          console.error('Error loading more results:', error);
        }
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [pipelineInstanceId, pageNumberNode, pageSizeNode, isLoadingMore, hasMorePages, mergeUniqueResults]);

  // Define CrudFunction first to avoid dependency issues
  const CrudFunction = useCallback(async (payload) => {
    if (selectedPipeline.id) {
      Object.assign(selectedPipeline, {
        ...payload,
        name: payload.name,
        description: payload.description || selectedPipeline.description
      });
      await updatePipeline(selectedPipeline);
    } else {
      Object.assign(selectedPipeline, {
        ...payload,
        description: payload.description || payload.name, // Use the provided description if available
        code: "Pipeline-" + new Date().getTime()
      });
      await createPipeline(selectedPipeline);
    }
  }, [selectedPipeline]);

  const validateMandetoryNodeProperties = () => {
    const mandetoryFieldConfig = mandatoryFields;

    validationLoop : for(const node of nodes) {
      const mandotoryFieldsForType = mandetoryFieldConfig[node?.type];
      if(mandotoryFieldsForType) {
        for(const field of mandotoryFieldsForType) {
          const value = node?.data?.data[field];
          if(!value || value == "") {
            const nodeName = convertNodeNameToLocal(node?.type);
            const fieldName = field
              .split('_')
              .map(e => e.charAt(0).toUpperCase() + e.slice(1))
              .join(' ');

            showToast({ 
              message: `The ${fieldName} field is mandatory inside the ${nodeName} to save the pipeline.`,
              type: "error",
              duration: 5000
            });

            return false;
          }
        }
      }
    }
    return true;
  }
  
  const savePipeline = useCallback(() => {
    console.log('Saving pipeline...', selectedPipeline);
    const isValidNodeProps = validateMandetoryNodeProperties();
    if(!isValidNodeProps) {
        console.log('Validation failed. Aborting save process.');
        return;
    }
    console.log('Pipeline description when saving:', selectedPipeline.description);
    setIsSaving(true);
    setError(null);
    
    try {
      if (!pipelineName.trim()) throw new Error('Pipeline name is required');

      const updatedEdges = edges.map(edge => ({
        ...edge,
        from: edge.source,
        to: edge.target,
        conditions: ["true"]
      }));

      const nodesForPayload = nodes.map(node => ({
        ...node.data,
        position: node.position,
        positionAbsolute: node.positionAbsolute ?? node.position
      }));

      const pipelineData = {
        name: pipelineName,
        nodes: nodesForPayload,
        edges: updatedEdges,
        description: selectedPipeline.description || '',
      };
      removeEmptyHeaders(pipelineData);
      CrudFunction(convertReactFlowToPipeline(pipelineData));
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [pipelineName, nodes, edges, selectedPipeline, CrudFunction, convertReactFlowToPipeline]);

  // const clearCanvas = useCallback(() => {
  //   if (window.confirm('Are you sure you want to clear the canvas? All unsaved changes will be lost.')) {
  //     setNodes([]);
  //     setEdges([]);
  //     setSelectedNode(null);
  //   }
  // }, []);

  const updateEdgeProperties = useCallback((id, data) => {
    setEdges((edges) =>
      edges.map((edge) =>
        edge.id === id
          ? { ...edge, data: { ...edge.data, ...data } }
          : edge
      )
    );
  }, [setEdges]);

  const handleNavigateAway = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    navigate('/agent-pipelines');
  };

 const removeEmptyHeaders = (pipelineData) => {
  try {
    pipelineData.nodes.forEach((node) => {
      if (node.type === "API" && node.data.headers) {
        for (let key in node.data.headers) {
          if (node.data.headers.hasOwnProperty(key) && (key === "" || node.data.headers[key] === "")) {
            delete node.data.headers[key]; 
          }
        }

        if (Object.keys(node.data.headers).length === 0) {
          delete node.data.headers; 
        }
      }
    });

  } catch (error) {
    console.error("Error while removing empty headers", error);
  }
}



  const [drawerOpen, setDrawerOpen] = useState(false);
  const [analyticsDrawerOpen, setAnalyticsDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(540);
  const [lastPipelineRunDetails, setLastPipeLIneRunDetails] = useState({});
  const [manualRun, setManulRun] = useState(true);
  const drawerResizing = useRef(false);

  const onDrawerMouseDown = () => {
    drawerResizing.current = true;
    document.body.style.cursor = 'ew-resize';
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (drawerResizing.current) {
        setDrawerWidth((prev) => Math.max(280, Math.min(700, prev - e.movementX)));
      }
    };
    const onMouseUp = () => {
      drawerResizing.current = false;
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);


  useEffect(() => {
    const getLastPipeline = async () => {
      const data = await getPipelineRuns({ code: pipelineId }, {created_at: "DESC"}, "skip=0&limit=1");
      if (data) {
        let payload = data[0]?.workflow_vars;
        if(payload){
          setLastPipeLIneRunDetails({ 'payload': payload });
        }
      }
    }

    getLastPipeline();
  }, [])


  const convertFilePathsWithFolder = (filePathArray) => {
    return filePathArray?.map(filePath => {
      const pathAfterRunpipeline = filePath.split('/runpipeline/')[1];

      return {
        name: pathAfterRunpipeline,
        size: 30000
      };
    });
  };


  const handleManualRun = (manualFl) => {
    setManulRun(manualFl);
    if (!manualFl) {
      const docUrl = lastPipelineRunDetails?.payload?.document_urls;
      setDocUrls(docUrl);
      const files = convertFilePathsWithFolder(docUrl)
      setUploadedDocuments(files)
      setPipelineInput(JSON.stringify(lastPipelineRunDetails.payload?.ingestion_request?.payload, null, 2))
    }
  }
  const temp = (testPayload) => {
    console.log("Pipeline properties updated:", testPayload);
    setSelectedPipeline(prevState => {
      // Create a new state object with all properties from both prevState and testPayload
      const newState = { 
        ...prevState,
        ...testPayload, 
        value_stream: testPayload.value_stream !== undefined ? testPayload.value_stream : prevState.value_stream,
        entity_name: testPayload.entity_name !== undefined ? testPayload.entity_name : prevState.entity_name,
        knowledgebase: testPayload.knowledgebase !== undefined ? testPayload.knowledgebase : prevState.knowledgebase,
      };

      if (testPayload.associated_entities !== undefined) {
        newState.associated_entities = testPayload.associated_entities.map(entity => {
          if (typeof entity === 'string') {
            return { entity_name: entity };
          }
          if (entity.name && !entity.entity_name) {
            return { entity_name: entity.name };
          }
          return entity;
        });
      } else {
        if (prevState.associated_entities && prevState.associated_entities.length > 0) {
          newState.associated_entities = prevState.associated_entities.map(entity => {
            if (typeof entity === 'string') {
              return { entity_name: entity };
            }
            if (entity.name && !entity.entity_name) {
              return { entity_name: entity.name };
            }
            return entity;
          });
        } else {
          newState.associated_entities = prevState.associated_entities || [];
        }
      }
      
      return newState;
    });
  }

  return (
    <PageContainer>
      <motion.div initial="hidden" animate="visible" variants={fadeIn} style={{ height: '100%' }}>
        <DesignerHeader>
          <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <PageTitle>
              {/* <PageIcon><FontAwesomeIcon icon={faChalkboardTeacher} /></PageIcon>
              &nbsp; */}
              <Button onClick={handleNavigateAway} secondary ><FontAwesomeIcon icon={faChevronCircleLeft} /></Button>&nbsp;
            </PageTitle>
            <PipelineName
              id="pipeline-name"
              type="text"
              value={pipelineName}
              onChange={(e) => setPipelineName(e.target.value)}
              placeholder="Enter pipeline name"
              style={{ flex: '1 1 auto' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* <label htmlFor="pipeline-name" style={{ marginRight: '0.5rem', fontSize: '0.9rem' }}>Pipeline Name:</label> */}

            <ActionButtons>
              {/* <PipelineButtons 
                onClick={clearCanvas}
                title="Clear"
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faBroom} />
              </PipelineButtons> */}
              {/* <PipelineButtons 
                onClick={handleNavigateAway}
                title="Cancel"
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </PipelineButtons> */}
              <PipelineButtons 
                onClick={openPropertiesWithoutNodeSelected}
                title="Open Pipeline Properties"
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faCog} />
              </PipelineButtons>
              <PipelineButtons 
                onClick={runPipeline} 
                disabled={isRunning}
                title={isRunning ? 'Running...' : 'Run Pipeline'}
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faPlay} />
                {isRunning && <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>...</span>}
              </PipelineButtons>
              <PipelineButtons 
                primary 
                onClick={savePipeline} 
                disabled={isSaving}
                title={isSaving ? 'Saving...' : 'Save Pipeline'}
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faSave} />
                {isSaving && <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>...</span>}
              </PipelineButtons>
            </ActionButtons>
            {/* {pollingInterval && (
              <div style={{ 
                marginLeft: '1rem',
                padding: '0.5rem 1rem',
                background: '#e3f2fd',
                borderRadius: '4px',
                fontSize: '0.9rem',
                color: '#1565c0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  background: '#4caf50'
                }}></div>
                Checking pipeline status...
              </div>
            )} */}
          </div>
        </DesignerHeader>

        {error && <div style={{ padding: '0.5rem', background: '#ffe0e0', color: '#b00020', borderRadius: '4px' }}>Error: {error}</div>}
        {isSuccess && <div style={{ padding: '0.5rem', background: '#d4edda', color: '#155724', borderRadius: '4px' }}>Pipeline saved successfully!</div>}

        <DesignerContainer style={{ width: 'calc(100vw - 320px)' }}>
          <NodePaletteContainer>
            <NodePalette />
          </NodePaletteContainer>
          <FlowContainer ref={reactFlowWrapper} >
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={onInit}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              fitView = {false}
              snapToGrid
              snapGrid={[15, 15]}
              defaultZoom={1.5}
              minZoom={0.2}
              maxZoom={4}
              deleteKeyCode="Delete"
            >
              <Controls />
              <MiniMap />
              <Background color="#f0f0f0" gap={16} />
            </ReactFlow>
          </FlowContainer>
          {/* <PropertiesPanel
            selectedNode={selectedNode}
            updateNodeProperties={updateNodeProperties}
            pipelineDescription={pipelineDescription}
            setPipelineDescription={setPipelineDescription}
            selectedEdge={selectedEdge}
            updateEdgeProperties={updateEdgeProperties}
          /> */}
        </DesignerContainer>
        {/* Properties Panel */}
        {pipelineRunFlag ? (
          <DrawerToggleButton
            onClick={() => setDrawerOpen((open) => !open)}
            drawerOpen={drawerOpen}
            drawerWidth={drawerWidth}
          >
            {drawerOpen ? (
              <FaTimes size={22} />
            ) : (
              <FaSlidersH size={22} />
            )}
          </DrawerToggleButton>
        ) : (<>
          <DrawerToggleButton
            onClick={() => setDrawerOpen((open) => !open)}
            drawerOpen={drawerOpen}
            drawerWidth={drawerWidth}
          >
            {drawerOpen ? (
              <FaTimes size={22} />
            ) : (
              <FaSlidersH size={22} />
            )}
          </DrawerToggleButton>
        </>)}

        <FloatingDrawer open={drawerOpen} width={drawerWidth} style={{ zIndex: 9991 }}>
          <DrawerResizer onMouseDown={onDrawerMouseDown} />
          <PropertiesPanel
            selectedNode={selectedNode}
            updateNodeProperties={updateNodeProperties}
            pipelineDescription={pipelineDescription}
            setPipelineDescription={setPipelineDescription}
            selectedPipeline={selectedPipeline}
            setSelectedPipeline={temp}
            selectedEdge={selectedEdge}
            updateEdgeProperties={updateEdgeProperties}
          />
        </FloatingDrawer>

        {/* Analytics Panel */}

        {pipelineRunFlag ? (
          <DrawerToggleButton
            onClick={() => setAnalyticsDrawerOpen((open) => !open)}
            drawerOpen={analyticsDrawerOpen}
            drawerWidth={drawerWidth}
          >
            {analyticsDrawerOpen ? (
              <FaTimes size={22} />
            ) : (
              <FaSlidersH size={22} />
            )}
          </DrawerToggleButton>
        ) : (<></>)}
        <FloatingDrawer open={analyticsDrawerOpen} width={drawerWidth} style={{ zIndex: 9991 }}>
          <DrawerResizer onMouseDown={onDrawerMouseDown} />
          <PipelineExecutionDrawer
            isOpen={analyticsDrawerOpen}
            onClose={() => setAnalyticsDrawerOpen(false)}
            onRefresh={() => {
              refreshResults()
            }}
            onResume={handleResumePipeline}
            onPaginationChange={handlePaginationChange}
            executionResults={executionResults}
            isRunning={isRunning}
            isLoadingMore={isLoadingMore}
            pipelineName={pipelineName}
            totalExecutionResults={totalExecutionResults}
            pipelineStatus={pipelineStatus}
            pollingInterval={pollingInterval}
            showCloseButton={true}
          />
        </FloatingDrawer>
      </motion.div>
      {showRunModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowRunModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '2rem',
              width: '80%'
            }}
          >
            <SubSectionTitle>Run Pipeline: {pipelineName}</SubSectionTitle>
            {pipelineStatus && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: pipelineStatus.transaction_status === 'COMPLETED' ? '#d4edda' :
                  ['ERROR', 'ERRORED'].includes(pipelineStatus.transaction_status?.toUpperCase()) ? '#f8d7da' :
                    '#f0f8ff',
                borderRadius: '4px',
                fontSize: '0.9rem',
                border: `1px solid ${pipelineStatus.transaction_status === 'COMPLETED' ? '#c3e6cb' :
                  ['ERROR', 'ERRORED'].includes(pipelineStatus.transaction_status?.toUpperCase()) ? '#f5c6cb' :
                    '#bee5eb'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>Status:</strong> {pipelineStatus.transaction_status || 'Unknown'}
                  </div>
                  {pollingInterval && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#4caf50'
                      }}></div>
                      Checking every 10s...
                    </div>
                  )}
                </div>
                {pipelineStatus.created_at && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                    Last updated: {new Date(pipelineStatus.created_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}
            <Divider />
            {runError && (
              <div style={{
                padding: '0.75rem',
                background: '#ffe0e0',
                color: '#b00020',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                Error: {runError}
              </div>
            )}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'top' }}>
              <div style={{ flex: '1 1 60%' }}>
                <div style={{ flex: '1 1 60%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: 500 }}>
                      Pipeline Input ({inputMode === 'json' ? 'JSON' : 'Markdown'})
                    </label>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="pipelineOption"
                          value="true"
                          checked={manualRun === true}
                          onChange={() => handleManualRun(true)}
                          style={{ marginRight: '0.25rem' }}
                        />
                        Manual Run
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="pipelineOption"
                          value="false"
                          checked={manualRun === false}
                          onChange={() => handleManualRun(false)}
                          style={{ marginRight: '0.25rem' }}
                        />
                        Use Previous Run Input
                      </label>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => {setInputMode('json'); setPipelineInput('{\n  "key": "value"\n}');}}
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        background: inputMode === 'json' ? '#007bff' : '#f8f9fa',
                        color: inputMode === 'json' ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      JSON
                    </button>
                    <button
                      type="button"
                      onClick={() => {setInputMode('markdown'); setPipelineInput('');}}
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        background: inputMode === 'markdown' ? '#007bff' : '#f8f9fa',
                        color: inputMode === 'markdown' ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Markdown
                    </button>
                  </div>
                  {inputMode === 'json' ? (
                    <Textarea
                      value={pipelineInput}
                      onChange={(e) => setPipelineInput(e.target.value)}
                      style={{
                        width: '100%',
                        height: '200px',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      }}
                      placeholder='Enter JSON input for the pipeline'
                    />
                  ) : (
                    <MarkdownEditorContainer>
                      <ToggleButton
                        active={isRunPreviewMode}
                        onClick={() => setIsRunPreviewMode(!isRunPreviewMode)}
                        type="button"
                      >
                        <FontAwesomeIcon icon={isRunPreviewMode ? faEdit : faEye} />
                        {isRunPreviewMode ? 'Edit' : 'Preview'}
                      </ToggleButton>
                      
                      {isRunPreviewMode ? (
                        <MarkdownPreview>
                          {pipelineInput ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {pipelineInput}
                            </ReactMarkdown>
                          ) : (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                              No content provided
                            </p>
                          )}
                        </MarkdownPreview>
                      ) : (
                        <Textarea
                          value={pipelineInput}
                          onChange={(e) => setPipelineInput(e.target.value || '')}
                          placeholder="Enter markdown content..."
                          style={{
                            width: '100%',
                            height: '200px',
                            padding: '0.75rem',
                            border: 'none',
                            borderRadius: '0',
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                            fontSize: '0.9rem',
                            resize: 'none',
                            outline: 'none'
                          }}
                        />
                      )}
                    </MarkdownEditorContainer>
                  )}
                </div>
              </div>

              <div style={{ flex: '1 1 auto' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Upload Document (Optional)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => {
                      if (e.target.files && e.target.files.length > 0) {
                        setUploadedDocuments(prev => {
                          const existing = new Set(prev.map(f => f.name + f.size));
                          const newFiles = Array.from(e.target.files).filter(
                            f => !existing.has(f.name + f.size)
                          );
                          return [...prev, ...newFiles];
                        });
                      }
                      /* Reset input */
                      e.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('file-upload').click()}
                    style={{ minWidth: 120 }}
                  >
                    Upload File(s)
                  </Button>
                </div>
                {uploadedDocuments && uploadedDocuments.length > 0 && (
                  <div
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      marginTop: '0.75rem'
                    }}
                  >
                    {uploadedDocuments.map((file, idx) => (
                      <div
                        key={file.name + file.size + idx}
                        style={{
                          background: '#e6f7ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                          marginTop: '.5rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 4,
                        }}
                      >
                        <span>
                          {file.name} <br /> <small>({Math.round(file.size / 1024)} KB)</small>
                        </span>
                        <Button
                          type="button"
                          style={{
                            background: '#ffeded',
                            color: '#b00020',
                            border: 'none',
                            borderRadius: 4,
                            padding: '2px 8px',
                            marginLeft: 8,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                          onClick={() =>
                            setUploadedDocuments(prev =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <PipelineButtons 
                onClick={() => setShowRunModal(false)} 
                title="Cancel"
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </PipelineButtons>
              {pollingInterval && (
                <PipelineButtons
                  onClick={stopPolling}
                  style={{ background: '#ff6b6b', color: 'white', minWidth: '40px', padding: '0.5rem' }}
                  title="Stop Polling"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </PipelineButtons>
              )}
              <PipelineButtons
                primary
                onClick={executePipeline}
                disabled={isRunning}
                title={isRunning ? 'Running...' : 'Run Pipeline'}
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faPlay} />
                {isRunning && <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>...</span>}
              </PipelineButtons>
            </div>
          </div>
        </Modal>
      )}
      {showResumeModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowResumeModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '2rem',
              width: '80%'
            }}
          >
            <SubSectionTitle>Resume Pipeline: {pipelineName}</SubSectionTitle>
            {pipelineStatus && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: pipelineStatus.transaction_status === 'COMPLETED' ? '#d4edda' :
                  ['ERROR', 'ERRORED'].includes(pipelineStatus.transaction_status?.toUpperCase()) ? '#f8d7da' :
                    '#f0f8ff',
                borderRadius: '4px',
                fontSize: '0.9rem',
                border: `1px solid ${pipelineStatus.transaction_status === 'COMPLETED' ? '#c3e6cb' :
                  ['ERROR', 'ERRORED'].includes(pipelineStatus.transaction_status?.toUpperCase()) ? '#f5c6cb' :
                    '#bee5eb'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>Status:</strong> {pipelineStatus.transaction_status || 'Unknown'}
                  </div>
                  {pollingInterval && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#4caf50'
                      }}></div>
                      Checking every 10s...
                    </div>
                  )}
                </div>
                {pipelineStatus.created_at && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                    Last updated: {new Date(pipelineStatus.created_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}
            <Divider />
            {runError && (
              <div style={{
                padding: '0.75rem',
                background: '#ffe0e0',
                color: '#b00020',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                Error: {runError}
              </div>
            )}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'top' }}>
              <div style={{ flex: '1 1 60%' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Pipeline Input ({inputMode === 'json' ? 'JSON' : 'Markdown'})
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={() => setInputMode('json')}
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      background: inputMode === 'json' ? '#007bff' : '#f8f9fa',
                      color: inputMode === 'json' ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    JSON
                  </button>
                  <button
                    type="button"
                    onClick={() => setInputMode('markdown')}
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      background: inputMode === 'markdown' ? '#007bff' : '#f8f9fa',
                      color: inputMode === 'markdown' ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Markdown
                  </button>
                </div>
                {inputMode === 'json' ? (
                  <Textarea
                    value={resumeInput}
                    onChange={(e) => setResumeInput(e.target.value)}
                    style={{
                      width: '100%',
                      height: '200px',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }}
                    placeholder='Enter JSON input for the pipeline'
                  />
                ) : (
                  <MarkdownEditorContainer>
                    <ToggleButton
                      active={isResumePreviewMode}
                      onClick={() => setIsResumePreviewMode(!isResumePreviewMode)}
                      type="button"
                    >
                      <FontAwesomeIcon icon={isResumePreviewMode ? faEdit : faEye} />
                      {isResumePreviewMode ? 'Edit' : 'Preview'}
                    </ToggleButton>
                    
                    {isResumePreviewMode ? (
                      <MarkdownPreview>
                        {resumeInput ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {resumeInput}
                          </ReactMarkdown>
                        ) : (
                          <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                            No content provided
                          </p>
                        )}
                      </MarkdownPreview>
                    ) : (
                      <Textarea
                        value={resumeInput}
                        onChange={(e) => setResumeInput(e.target.value || '')}
                        placeholder="Enter markdown content..."
                        style={{
                          width: '100%',
                          height: '200px',
                          padding: '0.75rem',
                          border: 'none',
                          borderRadius: '0',
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          fontSize: '0.9rem',
                          resize: 'none',
                          outline: 'none'
                        }}
                      />
                    )}
                  </MarkdownEditorContainer>
                )}
              </div>

              <div style={{ flex: '1 1 auto' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Upload Document (Optional)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <input
                    id="file-upload-resume"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => {
                      if (e.target.files && e.target.files.length > 0) {
                        setResumeUploadedDocuments(prev => {
                          const existing = new Set(prev.map(f => f.name + f.size));
                          const newFiles = Array.from(e.target.files).filter(
                            f => !existing.has(f.name + f.size)
                          );
                          return [...prev, ...newFiles];
                        });
                      }
                      /* Reset input */
                      e.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('file-upload-resume').click()}
                    style={{ minWidth: 120 }}
                  >
                    Upload File(s)
                  </Button>
                </div>
                {resumeUploadedDocuments && resumeUploadedDocuments.length > 0 && (
                  <div
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      marginTop: '0.75rem'
                    }}
                  >
                    {resumeUploadedDocuments.map((file, idx) => (
                      <div
                        key={file.name + file.size + idx}
                        style={{
                          background: '#e6f7ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                          marginTop: '.5rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 4,
                        }}
                      >
                        <span>
                          {file.name} <br /> <small>({Math.round(file.size / 1024)} KB)</small>
                        </span>
                        <Button
                          type="button"
                          style={{
                            background: '#ffeded',
                            color: '#b00020',
                            border: 'none',
                            borderRadius: 4,
                            padding: '2px 8px',
                            marginLeft: 8,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                          onClick={() =>
                            setResumeUploadedDocuments(prev =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <Button 
                onClick={() => setShowResumeModal(false)} 
                title="Cancel"
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
              {pollingInterval && (
                <Button
                  onClick={stopPolling}
                  style={{ background: '#ff6b6b', color: 'white', minWidth: '40px', padding: '0.5rem' }}
                  title="Stop Polling"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
              <Button
                primary
                onClick={resumePipelineExecution}
                disabled={isRunning}
                title={isRunning ? 'Resuming...' : 'Resume Pipeline'}
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faPlay} />
                {isRunning && <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>...</span>}
              </Button>
            </div>
          </div>
        </Modal>
      )}

    </PageContainer>
  );
};

export default PipelineDesigner;